import { type ChangeEvent, type FormEvent, type KeyboardEvent, type PointerEvent, useEffect, useRef, useState } from 'react';
import { trackEvent } from '@zep/logger/tracking';
import { AI_BUILDER_CHAT_PLACEHOLDER, AI_BUILDER_GUIDE_FALLBACK_URL } from '../consts';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { useAiBuilderChatActions, useAiBuilderChatComposerSelector, useAiBuilderChatComposerSignalState } from '../stores';
import { useAiBuilderActiveOperation } from '../stores/AiBuilderSessionProvider';
import { AI_BUILDER_COMPOSER_MAX_MESSAGE_LENGTH } from '../stores/types';
import { deriveAiBuilderComposerPolicy } from '../utils/aiBuilderComposerPolicy';
import { pageNumbersToRanges, rangesToPageNumbers } from '../utils/pdfPageRanges';
import { useAiBuilderAttachmentPreparation } from './AiBuilderAttachmentPreparationProvider';
import { AiBuilderComposerControlBar } from './AiBuilderComposerControlBar';
import { AiBuilderComposerShell } from './AiBuilderComposerShell';
import { AiBuilderFilePreviewCard } from './AiBuilderFilePreviewCard';
import { AiBuilderPdfPagePreviewList } from './AiBuilderPdfPagePreviewList';
import { AiBuilderSelectedChoiceChips } from './AiBuilderSelectedChoiceChips';
import { AiBuilderSelectedQuizContextChip } from './AiBuilderSelectedQuizContextChip';
export const AiBuilderComposer = ({
  dropOverlayVisible = false,
  shouldAutoFocus = true
}: {
  /** 패널 전체 파일 드래그 감지 상태 — AiBuilderChatPanelContent가 소유한다. */
  dropOverlayVisible?: boolean;
  /** 모바일 Drawer 입장 애니메이션이 끝난 뒤에만 keyboard를 연다. */
  shouldAutoFocus?: boolean;
}) => {
  const {
    draft,
    isGenerating,
    messages,
    panelOpen,
    placeholder,
    selectedFile,
    selectedQuizContext,
    turnInProgress,
    usage
  } = useAiBuilderChatComposerSelector();
  const {
    focusRequestId
  } = useAiBuilderChatComposerSignalState();
  const {
    cancelGeneration,
    clearSelectedQuizContext,
    requestConfirmActionAttention,
    setDraft,
    setSelectedFile,
    submitDraft
  } = useAiBuilderChatActions();
  const copy = useAiBuilderChatCopy();
  const {
    canOpenFilePicker,
    openFilePicker
  } = useAiBuilderAttachmentPreparation();
  const guideUrl = copy.composer.learnMoreUrl.startsWith('http') ? copy.composer.learnMoreUrl : AI_BUILDER_GUIDE_FALLBACK_URL;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [isDraftFocused, setIsDraftFocused] = useState(false);
  const [isSourceInputTipVisible, setIsSourceInputTipVisible] = useState(false);
  const generatorRunning = useAiBuilderActiveOperation() === 'generator';
  const composerPolicy = deriveAiBuilderComposerPolicy({
    draft,
    hasSelectedFile: !!selectedFile,
    isGenerating,
    messages,
    placeholder,
    copy,
    turnInProgress,
    usage,
    generatorRunning
  });
  const {
    activeChoiceMessage,
    activeConfirmMessage,
    isClosedActionMode
  } = composerPolicy;
  const hasActiveChoiceMessage = !!activeChoiceMessage;
  const hasActiveConfirmMessage = !!activeConfirmMessage;
  const shouldShowSourceInputTooltip = composerPolicy.interactionMode === 'freeText' && !composerPolicy.usageBlocked && isDraftFocused && isSourceInputTipVisible && draft.trim().length === 0 && (placeholder === AI_BUILDER_CHAT_PLACEHOLDER.youtube || placeholder === AI_BUILDER_CHAT_PLACEHOLDER.website);
  useEffect(() => {
    if (!panelOpen) return;
    if (!shouldAutoFocus) return;
    if (isClosedActionMode) return;
    textareaRef.current?.focus();
  }, [focusRequestId, hasActiveChoiceMessage, hasActiveConfirmMessage, isClosedActionMode, panelOpen, shouldAutoFocus]);
  useEffect(() => {
    if (!shouldAllowSourceInputTip({
      draft,
      interactionMode: composerPolicy.interactionMode,
      placeholder
    })) {
      setIsSourceInputTipVisible(false);
      return;
    }
    if (!isDraftFocused) return;
    setIsSourceInputTipVisible(true);
    const timeoutId = window.setTimeout(() => {
      setIsSourceInputTipVisible(false);
    }, 3000);
    return () => window.clearTimeout(timeoutId);
  }, [composerPolicy.interactionMode, draft, isDraftFocused, placeholder]);
  const handleDraftChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (!composerPolicy.canEditDraft) return;
    setIsSourceInputTipVisible(false);
    setDraft(event.currentTarget.value);
    scrollComposerCaretIntoView();
  };

  /**
   * 입력 영역 스크롤은 셸 컨테이너(max-h 230px)가 담당하므로(37421:22726),
   * 문서 끝에서 타이핑할 때 autoSize로 늘어난 캐럿 줄이 컨테이너 밖으로
   * 밀려나지 않게 바닥으로 따라가 준다. 중간 편집 시에는 건드리지 않는다.
   */
  const scrollComposerCaretIntoView = () => {
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      if (textarea.selectionEnd !== textarea.value.length) return;
      const scrollArea = textarea.closest('[data-ai-builder-composer-input-header]');
      if (!(scrollArea instanceof HTMLElement)) return;
      scrollArea.scrollTop = scrollArea.scrollHeight;
    });
  };
  const submitComposer = () => {
    if (isGenerating) {
      cancelGeneration();
      return;
    }
    if (turnInProgress) return;
    if (!composerPolicy.canSubmit) return;
    submitDraft();
    textareaRef.current?.focus();
  };
  const handleDraftKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter') return;
    if (event.shiftKey) return;
    if (event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (composerPolicy.hasActiveTurn) return;
    submitComposer();
  };
  const handleOpenFilePicker = () => {
    if (!composerPolicy.canUseSourceAttachment || !canOpenFilePicker) return;
    openFilePicker();
  };
  const removePdfPage = (pageNumber: number) => {
    if (!selectedFile || selectedFile.pageRanges === undefined) return;
    const remainingPages = rangesToPageNumbers(selectedFile.pageRanges).filter(page => page !== pageNumber);
    if (remainingPages.length === 0) {
      setSelectedFile(null);
      return;
    }
    setSelectedFile({
      ...selectedFile,
      pageRanges: pageNumbersToRanges(remainingPages)
    });
  };
  const handleClosedActionInputPointerDown = (event: PointerEvent<HTMLTextAreaElement>) => {
    if (!isClosedActionMode || !activeConfirmMessage) return;
    event.preventDefault();
    requestConfirmActionAttention(activeConfirmMessage.id);
  };
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitComposer();
  };
  return <form className="flex w-full flex-col gap-sm" onSubmit={handleSubmit} data-sentry-component="AiBuilderComposer" data-sentry-source-file="AiBuilderComposer.tsx">
      <AiBuilderComposerShell intent={panelOpen ? 'strong' : 'neutral'} value={draft} muted={isClosedActionMode || composerPolicy.usageBlocked} dropOverlayVisible={dropOverlayVisible} data-sentry-element="AiBuilderComposerShell" data-sentry-source-file="AiBuilderComposer.tsx">
        {selectedFile ? <AiBuilderComposerShell.AttachmentSlot>
            {selectedFile.pageRanges !== undefined ? <AiBuilderPdfPagePreviewList copy={copy.attachmentPreview} file={selectedFile.file} pageCount={selectedFile.pageCount} pageRanges={selectedFile.pageRanges} onRemovePage={composerPolicy.hasActiveTurn ? undefined : removePdfPage} size="composer" /> : <ol>
                <li>
                  <AiBuilderFilePreviewCard copy={copy.attachmentPreview} file={selectedFile.file} onRemove={composerPolicy.hasActiveTurn ? undefined : () => setSelectedFile(null)} size="composer" />
                </li>
              </ol>}
          </AiBuilderComposerShell.AttachmentSlot> : null}
        <AiBuilderComposerShell.TopSlot data-sentry-element="unknown" data-sentry-source-file="AiBuilderComposer.tsx">
          {activeChoiceMessage && <AiBuilderSelectedChoiceChips message={activeChoiceMessage} />}
        </AiBuilderComposerShell.TopSlot>

        <AiBuilderComposerShell.Field ref={textareaRef} value={draft} aria-disabled={isClosedActionMode || composerPolicy.usageBlocked ? true : undefined} onChange={handleDraftChange} onFocus={() => setIsDraftFocused(true)} onBlur={() => {
        setIsDraftFocused(false);
        setIsSourceInputTipVisible(false);
      }} onKeyDown={handleDraftKeyDown} onPointerDown={handleClosedActionInputPointerDown} placeholder={composerPolicy.placeholder} disabled={composerPolicy.usageBlocked} readOnly={!composerPolicy.canEditDraft} tabIndex={isClosedActionMode ? -1 : undefined} maxLength={AI_BUILDER_COMPOSER_MAX_MESSAGE_LENGTH} aria-invalid={composerPolicy.isDraftOverMessageLengthLimit} sourceInputTooltip={{
        open: shouldShowSourceInputTooltip,
        label: copy.composer.sourceInputTooltip
      }} autoSize
      // 브라우저 기본 rows(2)가 빈 상태 scrollHeight를 2줄로 부풀려
      // autoSize가 1줄로 줄지 못한다. 디자인상 빈 입력창은 1줄 높이.
      rows={1} minRows={1}
      // maxRows 없음 — 시안(37421:22726)상 스크롤은 셸 입력 영역(max-h 230px)이 담당한다.
      // shrink-0: flex-shrink가 autoSize 높이를 컨테이너(230px)에 맞춰 짜부라뜨리면
      // 셸 스크롤이 생기지 않고 내용이 잘린다. 높이는 훅이, 스크롤은 셸이 소유한다.
      className="min-h-[28px] shrink-0 bg-transparent pt-3xs text-heading-sm-bold text-default placeholder:text-heading-sm-bold placeholder:text-disabled read-only:cursor-pointer read-only:bg-transparent" data-sentry-element="unknown" data-sentry-source-file="AiBuilderComposer.tsx" />

        <AiBuilderComposerShell.BottomBar data-sentry-element="unknown" data-sentry-source-file="AiBuilderComposer.tsx">
          <AiBuilderComposerControlBar attachmentPresent={!!selectedFile} canOpenFilePicker={canOpenFilePicker} canSubmit={composerPolicy.canSubmit} canUseSourceAttachment={composerPolicy.canUseSourceAttachment && !composerPolicy.hasActiveTurn} hasActiveTurn={composerPolicy.hasActiveTurn} isGenerating={isGenerating} leftAccessory={selectedQuizContext ? <AiBuilderSelectedQuizContextChip context={selectedQuizContext} onClear={clearSelectedQuizContext} /> : null} onOpenFilePicker={handleOpenFilePicker} remainingTurnsLabel={composerPolicy.remainingTurnsLabel} data-sentry-element="AiBuilderComposerControlBar" data-sentry-source-file="AiBuilderComposer.tsx" />
        </AiBuilderComposerShell.BottomBar>
      </AiBuilderComposerShell>
      <p className="flex w-full items-center justify-center gap-x-2xs text-caption-lg-medium text-assistive">
        <span className="min-w-0 truncate">{copy.composer.disclaimer}</span>
        <a href={guideUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 text-link" onClick={() => {
        trackEvent('quiz_ai_builder_guide_click', {
          url: guideUrl
        });
      }}>
          {copy.composer.learnMore}
        </a>
      </p>
    </form>;
};
const shouldAllowSourceInputTip = ({
  draft,
  interactionMode,
  placeholder
}: {
  draft: string;
  interactionMode: ReturnType<typeof deriveAiBuilderComposerPolicy>['interactionMode'];
  placeholder: (typeof AI_BUILDER_CHAT_PLACEHOLDER)[keyof typeof AI_BUILDER_CHAT_PLACEHOLDER];
}) => interactionMode === 'freeText' && draft.trim().length === 0 && (placeholder === AI_BUILDER_CHAT_PLACEHOLDER.youtube || placeholder === AI_BUILDER_CHAT_PLACEHOLDER.website);